import React from 'react';
import { observer } from 'mobx-react';

import InfoIcon from '@mui/icons-material/Info';

import { UuidableName } from 'utils/api/types';

import { EntityModules, ProtectorType } from 'core/constants';

import EventDetailsStore from 'stores/EventDetails/EventDetails';

import { useStore } from 'utils/hooks/store';

import { Button } from 'components/Button';

import { EntityDetailsType } from 'utils/api/modules/entities/entities.types';

import { EntitiesStores } from '../types';

import {
  EntityValues,
  evaluateRule,
  explain,
  hasRule,
  triggersOf,
  withAdjacency,
} from 'containers/PipelinesDetails/components/conditions/model';
import {
  EmptyStateBody,
  EmptyStateContainer,
  EmptyStateTitle,
  StatusBarLead,
  VisuallyHidden,
} from 'containers/PipelinesDetails/components/conditions/styles';

import { FieldNote, FieldNotesContext } from './WhyShown';

import { BackgroundInformationSections, ShowRelevantFieldsContainer } from './styles';
import EntitySection from './EntitySection';

// Two things that carried configured state now carry current state: which fields render, and
// `requiredFieldKeys`. Without the second a hidden field would still be demanded at the transition,
// breaking the invariant that no hidden field is ever required.

type EventDetailsTabProps = {
  store: EventDetailsStore;
  details: EntityDetailsType;
};

const EventDetailsTab: React.FunctionComponent<EventDetailsTabProps> = ({ store, details }) => {
  const { session, entityFields } = useStore();
  const [showRelevantFields, setShowRelevantFields] = React.useState(true);
  const announcedRef = React.useRef<Set<string> | null>(null);
  const [announcement, setAnnouncement] = React.useState('');

  const conditionsEnabled = Boolean(session.user?.conditionalStageFieldsEnabled);

  const currentStageValue = details.entity[store.stageField?.key as string];

  const stageRelevantFields = store.pipeline?.stages.find(
    (stage) => stage.uuid === (currentStageValue?.value as UuidableName)?.uuid
  )?.relevantFields;
  const relevantFields = stageRelevantFields ?? [];

  const fieldsById = new Map((entityFields.data ?? []).map((field) => [field.id, field]));

  const values: EntityValues = Object.fromEntries(
    Object.entries(details.entity).map(([key, item]) => [
      key,
      {
        value: item?.value,
        protectorType: fieldsById.get(key)?.protectorType as ProtectorType | undefined,
      },
    ])
  );

  const shownFields = conditionsEnabled
    ? relevantFields.filter((field) => evaluateRule(field.conditions, values))
    : relevantFields;
  const shownKeys = new Set(shownFields.map((field) => field.key));

  const noFieldsConfigured = stageRelevantFields?.length === 0;
  const allConditionsUnmatched = conditionsEnabled && relevantFields.length > 0 && shownKeys.size === 0;
  const anyFieldHiddenByCondition = relevantFields.some((field) => hasRule(field) && !shownKeys.has(field.key));

  const requiredFieldKeys = new Set(shownFields.filter((field) => field.required).map((field) => field.key));

  const fieldName = (key: string): string => fieldsById.get(key)?.name ?? key;

  React.useEffect(() => {
    if (noFieldsConfigured) {
      setShowRelevantFields(false);
    }
  }, [noFieldsConfigured]);

  const shownSignature = [...shownKeys].sort().join('|');

  React.useEffect(() => {
    const previous = announcedRef.current;
    announcedRef.current = shownKeys;
    if (!previous || !conditionsEnabled) {
      return;
    }
    const added = [...shownKeys].filter((key) => !previous.has(key));
    const removed = [...previous].filter((key) => !shownKeys.has(key));
    if (added.length) {
      setAnnouncement(`${added.map(fieldName).join(', ')} now ${added.length === 1 ? 'applies' : 'apply'}`);
    } else if (removed.length) {
      setAnnouncement(`${removed.map(fieldName).join(', ')} no longer ${removed.length === 1 ? 'applies' : 'apply'}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownSignature, conditionsEnabled]);

  const fieldNotes: Record<string, FieldNote> = {};
  if (conditionsEnabled && showRelevantFields) {
    details.layout.order.forEach((sectionKey) => {
      const section = details.layout.properties[sectionKey];
      if (!section) return;
      const inSection = new Set(section.order.filter((key) => shownKeys.has(key)));
      inSection.forEach((key) => {
        const field = shownFields.find((candidate) => candidate.key === key);
        const triggers = field && hasRule(field) ? triggersOf(field) : [];
        if (!field || triggers.length === 0 || triggers.some((trigger) => inSection.has(trigger))) {
          return;
        }
        fieldNotes[key] = {
          why: { triggerName: fieldName(triggers[0]), explanation: explain(field, values, fieldName) },
          fieldKey: key,
        };
      });
    });
  } else if (conditionsEnabled) {
    relevantFields.forEach((field) => {
      if (hasRule(field) && !shownKeys.has(field.key)) {
        fieldNotes[field.key] = { notAsked: true, fieldKey: field.key };
      }
    });
  }

  return (
    <FieldNotesContext.Provider value={fieldNotes}>
      <BackgroundInformationSections>
        <VisuallyHidden role="status" aria-live="polite" data-testid="conditional-fields-announcer">
          {announcement}
        </VisuallyHidden>

        {!noFieldsConfigured && !(allConditionsUnmatched && showRelevantFields) && (
          <ShowRelevantFieldsContainer data-testid="relevant-fields-status-bar">
            <StatusBarLead>
              <InfoIcon color="warning" />
              {showRelevantFields ? (
                <p>
                  {conditionsEnabled && anyFieldHiddenByCondition
                    ? 'Showing fields that apply to this event at this stage'
                    : 'Showing fields relevant to this stage'}
                </p>
              ) : (
                <p>Showing all event fields</p>
              )}
            </StatusBarLead>
            <Button
              variant="text"
              size="small"
              data-testid="relevant-fields-toggle"
              onClick={() => setShowRelevantFields(!showRelevantFields)}
            >
              {showRelevantFields ? 'Show all fields' : 'Show only relevant fields'}
            </Button>
          </ShowRelevantFieldsContainer>
        )}

        {allConditionsUnmatched && showRelevantFields ? (
          <EmptyStateContainer data-testid="conditional-fields-empty-state">
            <EmptyStateTitle>No fields apply to this event at this stage</EmptyStateTitle>
            <EmptyStateBody>Fields at this stage appear once you answer the questions they depend on.</EmptyStateBody>
            <Button variant="text" size="small" onClick={() => setShowRelevantFields(false)}>
              Show all fields
            </Button>
          </EmptyStateContainer>
        ) : (
          <div key={showRelevantFields ? 'relevant' : 'all'}>
            {details.layout.order.map((key) => {
              const section = details.layout.properties[key];
              if (!section) return null;

              const fieldOrder = showRelevantFields
                ? withAdjacency(
                    section.order.filter((field) => shownKeys.has(field)),
                    shownFields
                  )
                : section.order;

              if (fieldOrder.length === 0) {
                return null;
              }

              return (
                <EntitySection
                  key={key}
                  sectionKey={key}
                  title={section.title}
                  fieldOrder={fieldOrder}
                  store={store}
                  requiredFieldKeys={requiredFieldKeys}
                />
              );
            })}
          </div>
        )}
      </BackgroundInformationSections>
    </FieldNotesContext.Provider>
  );
};

const ObservedEventDetailsTab = observer(EventDetailsTab);

const EntityDetailsTab: React.FunctionComponent<{ store: EntitiesStores }> = ({ store }): React.ReactElement => {
  const { details } = store;
  if (!details) {
    return <></>;
  }

  // Narrowed here, so the event component's hooks all run unconditionally.
  if (store instanceof EventDetailsStore) {
    return <ObservedEventDetailsTab store={store} details={details} />;
  }

  return (
    <BackgroundInformationSections $flush={store.entityType === EntityModules.Audits}>
      {details.layout.order.map((key) => {
        const section = details.layout.properties[key];
        if (!section) return null;
        return (
          <EntitySection key={key} sectionKey={key} title={section.title} fieldOrder={section.order} store={store} />
        );
      })}
    </BackgroundInformationSections>
  );
};

export default observer(EntityDetailsTab);
